/**
 * Main App Component
 * Sets up routing, auth provider, and global UI providers.
 * Uses React.lazy() for route-based code splitting to reduce initial bundle size.
 */

import { Suspense, lazy, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { MotionRoot } from "@/components/motion/MotionRoot";
import { ViewTransitionLinks } from "@/components/motion/ViewTransitionLinks";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { ThemeProvider } from "@/hooks/useThemeProvider";
import { CookieConsentBanner } from "./components/CookieConsentBanner";
import { HoverDiagnostics } from "@/components/HoverDiagnostics";
import { HoverDebugBar } from "@/components/HoverDebugBar";

// Lazy load pages for code splitting - reduces initial bundle size
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const About = lazy(() => import("./pages/About"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const TeacherDashboard = lazy(() => import("./pages/TeacherDashboard"));
const TeacherTools = lazy(() => import("./pages/TeacherTools"));
const TeacherSettings = lazy(() => import("./pages/TeacherSettings"));
const TeacherSchoolYear = lazy(() => import("./pages/TeacherSchoolYear"));
const SpellingGrader = lazy(() => import("./pages/SpellingGrader"));
const StudentDashboard = lazy(() => import("./pages/StudentDashboard"));
const StudentDetail = lazy(() => import("./pages/StudentDetail"));
const ClassBehavior = lazy(() => import("./pages/ClassBehavior"));
const ReadingFluency = lazy(() => import("./pages/ReadingFluency"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ParentReport = lazy(() => import("./pages/ParentReport"));
const SharedMap = lazy(() => import("./pages/SharedMap"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const StudentNews = lazy(() => import("./pages/StudentNews"));
const ArticleView = lazy(() => import("./pages/ArticleView"));
const BehaviorBoardPage = lazy(() => import("./pages/BehaviorBoardPage"));
const TeacherDictations = lazy(() => import("./pages/TeacherDictations"));
const CreateDictation = lazy(() => import("./pages/CreateDictation"));
const DictationResults = lazy(() => import("./pages/DictationResults"));
const EditDictation = lazy(() => import("./pages/EditDictation"));
const StudentDictationTest = lazy(() => import("./pages/StudentDictationTest"));
const TeacherNotifications = lazy(() => import("./pages/TeacherNotifications"));
const StudentBookList = lazy(() => import("./pages/StudentBookList"));
const StudentBookEditor = lazy(() => import("./pages/StudentBookEditor"));
const StudentBookReader = lazy(() => import("./pages/StudentBookReader"));

// Shared cache: data stays fresh 1 min, kept 10 min, no refetch on every tab focus
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Minimal loading fallback that doesn't affect layout
const PageLoader = () => (
  <div className="min-h-dvh flex items-center justify-center bg-background">
    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
  </div>
);

// Inner app component that can use hooks
function AppContent() {
  const { loading: authLoading } = useAuth();

  const debugHoverEnabled =
    typeof window !== "undefined" && new URLSearchParams(window.location.search).has("debugHover");

  const hoverDebugEnabled =
    typeof window !== "undefined" && new URLSearchParams(window.location.search).has("hoverDebug");

  // Gate all routing until auth state is resolved to prevent flash of landing page
  if (authLoading) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      <Toaster />
      <Sonner />

      {/* App routing */}
      <BrowserRouter>
        <ViewTransitionLinks />
        {/* Hover diagnostics overlay (opt-in via ?debugHover=1) */}
        <HoverDiagnostics enabled={debugHoverEnabled} />

        {/* Live elementFromPoint debug bar (opt-in via ?hoverDebug=1) */}
        <HoverDebugBar enabled={hoverDebugEnabled} />

        {/* GDPR Cookie Consent Banner - temporarily disabled for testing */}
        {/* <CookieConsentBanner /> */}
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/about" element={<About />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />

            {/* Admin routes */}
            <Route path="/admin" element={<AdminDashboard />} />

            {/* Protected routes - role-specific dashboards */}
            <Route path="/teacher" element={<TeacherDashboard />} />
            <Route path="/teacher/tools" element={<TeacherTools />} />
            <Route path="/teacher/settings" element={<TeacherSettings />} />
            <Route path="/teacher/school-year" element={<TeacherSchoolYear />} />
            <Route path="/teacher/spelling-grader" element={<SpellingGrader />} />
            <Route path="/teacher/student/:studentId" element={<StudentDetail />} />
            <Route path="/teacher/sel" element={<ClassBehavior />} />
            <Route path="/teacher/behavior-board" element={<BehaviorBoardPage />} />
            <Route path="/teacher/reading" element={<ReadingFluency />} />
            <Route path="/teacher/dictations" element={<TeacherDictations />} />
            <Route path="/teacher/dictations/new" element={<CreateDictation />} />
            <Route path="/teacher/dictations/:id/results" element={<DictationResults />} />
            <Route path="/teacher/dictations/:id/edit" element={<EditDictation />} />
            <Route path="/teacher/notifications" element={<TeacherNotifications />} />
            <Route path="/student" element={<StudentDashboard />} />
            <Route path="/student/dictation/:id" element={<StudentDictationTest />} />
            <Route path="/student/books" element={<StudentBookList />} />
            <Route path="/student/books/:id" element={<StudentBookEditor />} />
            <Route path="/student/books/:id/read" element={<StudentBookReader />} />

            {/* Public parent report route */}
            <Route path="/parent/report/:token" element={<ParentReport />} />

            {/* Public shared map route */}
            <Route path="/shared-map/:token" element={<SharedMap />} />

            {/* Public news routes */}
            <Route path="/news" element={<StudentNews />} />
            <Route path="/news/:articleId" element={<ArticleView />} />

            {/* Catch-all for 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </>
  );
}

const App = () => {
  const noTooltips =
    typeof window !== "undefined" && new URLSearchParams(window.location.search).has("noTooltips");

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={noTooltips ? 99999 : undefined}>
        {/* Auth provider wraps entire app for authentication state */}
        <AuthProvider>
          {/* Theme provider for consistent theming across routes */}
          <ThemeProvider>
            <MotionRoot>
              <AppContent />
            </MotionRoot>
          </ThemeProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
